import { apiClient } from "../../lib/axios";
import type {
  MetricsFilters,
  MetricsStatsParams,
  MetricsStatsResponse,
  MetricsTrendsParams,
  MetricsTrendsResponse,
  MetricsDistributionParams,
  MetricsDistributionResponse,
  MetricsPatternsParams,
  MetricsPatternsResponse,
  MetricsComparisonParams,
  MetricsComparisonResponse,
  MetricsOverallRangeParams,
  MetricsOverallRangeResponse,
} from "./types";

const buildQueryParams = (params?: MetricsFilters) => {
  const queryParams: Record<string, unknown> = {};

  if (params?.start_date) {
    queryParams.start_date = params.start_date;
  }
  if (params?.end_date) {
    queryParams.end_date = params.end_date;
  }
  if (params?.department && params.department.length > 0) {
    queryParams.department = params.department;
  }
  if (params?.age_range && params.age_range.length > 0) {
    queryParams.age_range = params.age_range;
  }
  if (params?.sex) {
    queryParams.sex = params.sex;
  }
  if (params?.shift_pattern && params.shift_pattern.length > 0) {
    queryParams.shift_pattern = params.shift_pattern;
  }
  if (params?.user_ids && params.user_ids.length > 0) {
    queryParams.user_ids = params.user_ids;
  }
  if (params?.client_id) {
    queryParams.client_id = params.client_id;
  }
  if (params?.bypass_anonymisation) {
    queryParams.bypass_anonymisation = true;
  }

  return queryParams;
};

export const metricsApi = {
  getStats: async (
    params?: MetricsStatsParams
  ): Promise<MetricsStatsResponse> => {
    const queryParams = buildQueryParams(params);
    if (params?.metrics && params.metrics.length > 0) {
      queryParams.metrics = params.metrics.join(",");
    }

    const url = params?.client_id
      ? "/admin/client-metrics/stats"
      : "/admin/metrics/stats";
    const response = await apiClient.get(url, {
      params: queryParams,
    });
    return response.data;
  },

  getTrends: async (
    params: MetricsTrendsParams
  ): Promise<MetricsTrendsResponse> => {
    const queryParams = buildQueryParams(params);
    queryParams.metrics = params.metrics.join(",");
    if (params.granularity) {
      queryParams.granularity = params.granularity;
    }

    const url = params?.client_id
      ? "/admin/client-metrics/trends"
      : "/admin/metrics/trends";
    const response = await apiClient.get(url, {
      params: queryParams,
    });
    return response.data;
  },

  getDistribution: async (
    params: MetricsDistributionParams
  ): Promise<MetricsDistributionResponse> => {
    const queryParams = buildQueryParams(params);
    queryParams.metric = params.metric;

    const url = params?.client_id
      ? "/admin/client-metrics/distribution"
      : "/admin/metrics/distribution";
    const response = await apiClient.get(url, {
      params: queryParams,
    });
    return response.data;
  },

  getPatterns: async (
    params: MetricsPatternsParams
  ): Promise<MetricsPatternsResponse> => {
    const queryParams = buildQueryParams(params);
    queryParams.metric = params.metric;
    queryParams.granularity = params.granularity;
    if (params.date) {
      queryParams.date = params.date;
    }

    const url = params?.client_id
      ? "/admin/client-metrics/patterns"
      : "/admin/metrics/patterns";
    const response = await apiClient.get(url, {
      params: queryParams,
    });
    return response.data;
  },

  getComparison: async (
    params: MetricsComparisonParams
  ): Promise<MetricsComparisonResponse> => {
    const queryParams = buildQueryParams(params);
    queryParams.group_by = params.group_by;
    queryParams.metrics = params.metrics.join(",");

    const url = params?.client_id
      ? "/admin/client-metrics/comparison"
      : "/admin/metrics/comparison";
    const response = await apiClient.get(url, {
      params: queryParams,
    });
    return response.data;
  },

  getOverallRange: async (
    params: MetricsOverallRangeParams
  ): Promise<MetricsOverallRangeResponse> => {
    const response = await apiClient.get("/admin/metrics/overall-range", {
      params: { client_id: params.client_id },
    });
    return response.data;
  },
};
